import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { navLinks } from "@/mocks/site";
import { useFavorites } from "@/hooks/useFavorites";

export default function SiteHeader() {
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const { favorites } = useFavorites();

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const handleSearch = (event: React.FormEvent) => {
    event.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : "/search");
    setMenuOpen(false);
  };

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `relative py-1 text-sm transition-colors ${
      isActive
        ? "text-accent-600 font-bold"
        : "text-foreground-700 hover:text-primary-600"
    }`;

  return (
    <header className="sticky top-0 z-40 w-full border-b border-background-200 bg-background-50/95 backdrop-blur">
      <div className="mx-auto flex h-16 w-full items-center gap-4 px-4 md:h-20 md:px-6">
        <Link to="/" className="flex shrink-0 items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-background-50 md:h-10 md:w-10">
            <i className="ri-film-fill text-lg md:text-xl" />
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading text-lg font-bold tracking-wide text-foreground-950 md:text-xl">
              国产自拍
            </span>
            <span className="hidden text-[10px] tracking-[0.2em] text-foreground-500 md:block">
              MADE IN CHINA FILM
            </span>
          </span>
        </Link>

        <nav className="hidden items-center gap-7 md:flex">
          {navLinks.map((link) => (
            <NavLink key={link.path} to={link.path} className={linkClass} end={link.path === "/"}>
              {link.label}
            </NavLink>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-2 md:gap-3">
          <form onSubmit={handleSearch} className="hidden lg:flex">
            <div className="flex items-center gap-2 rounded-md border border-background-300 bg-background-100 px-3 py-2 transition-colors focus-within:border-primary-400">
              <i className="ri-search-line text-sm text-foreground-500" />
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                type="text"
                name="keyword"
                placeholder="搜索影视作品…"
                className="w-36 bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400 xl:w-48"
              />
            </div>
          </form>

          <Link
            to="/search"
            aria-label="搜索"
            className="flex h-10 w-10 items-center justify-center rounded-md border border-background-300 text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600 lg:hidden"
          >
            <i className="ri-search-line text-lg" />
          </Link>

          <Link
            to="/about"
            className="relative hidden h-10 w-10 items-center justify-center rounded-md border border-background-300 text-foreground-700 transition-colors hover:border-accent-400 hover:text-accent-600 md:flex"
            aria-label="我的收藏"
            title="我的收藏"
          >
            <i className="ri-heart-line text-lg" />
            {favorites.length > 0 && (
              <span className="absolute -right-1.5 -top-1.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-accent-500 px-1 text-[10px] font-bold text-background-50">
                {favorites.length}
              </span>
            )}
          </Link>

          <button
            type="button"
            onClick={() => setMenuOpen(true)}
            aria-label="打开菜单"
            className="flex h-10 w-10 items-center justify-center rounded-md border border-background-300 text-foreground-800 md:hidden"
          >
            <i className="ri-menu-line text-xl" />
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div
            className="absolute inset-0 bg-foreground-950/50"
            onClick={() => setMenuOpen(false)}
          />
          <div className="absolute right-0 top-0 h-full w-[78%] max-w-xs animate-fadeIn bg-background-50 p-5">
            <div className="mb-6 flex items-center justify-between">
              <span className="font-heading text-lg font-bold text-foreground-950">
                国产自拍
              </span>
              <button
                type="button"
                onClick={() => setMenuOpen(false)}
                aria-label="关闭菜单"
                className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300 text-foreground-700"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>

            <form onSubmit={handleSearch} className="mb-6">
              <div className="flex items-center gap-2 rounded-md border border-background-300 bg-background-100 px-3 py-2.5">
                <i className="ri-search-line text-sm text-foreground-500" />
                <input
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  type="text"
                  name="keyword"
                  placeholder="搜索影视作品…"
                  className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
                />
              </div>
            </form>

            <nav className="flex flex-col">
              {navLinks.map((link) => (
                <NavLink
                  key={link.path}
                  to={link.path}
                  end={link.path === "/"}
                  onClick={() => setMenuOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center justify-between border-b border-background-200 py-3.5 text-base ${
                      isActive ? "font-bold text-accent-600" : "text-foreground-800"
                    }`
                  }
                >
                  {link.label}
                  <i className="ri-arrow-right-s-line text-foreground-400" />
                </NavLink>
              ))}
            </nav>

            <Link
              to="/about"
              onClick={() => setMenuOpen(false)}
              className="mt-6 flex items-center justify-center gap-2 rounded-md bg-primary-500 py-3 text-sm font-medium text-background-50"
            >
              <i className="ri-heart-line" />
              我的收藏（{favorites.length}）
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}